import { useEffect, useState, useCallback } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BackgroundLoaderProvider } from '../contexts/BackgroundLoaderContext';
import { VocabProvider } from '../features/articles/VocabContext';
import { useTranslation } from '../i18n/I18nContext';
import { LoadingBar } from '../components/ui/loading-bar';
import Layout from '../components/Layout';

export default function OnboardedLayout() {
  const { user, isSessionValid, loading, fetchUserAccount } = useAuth();
  const { t } = useTranslation();
  // Start as true when session exists but user hasn't been fetched yet —
  // prevents a flash of the onboarding screen before fetchUserAccount completes.
  const [userDataLoading, setUserDataLoading] = useState(!user && isSessionValid && !loading);
  const [fetchError, setFetchError] = useState(false);
  const loadUserData = useCallback(async () => {
    if (!isSessionValid || user || loading) return;
    setUserDataLoading(true);
    setFetchError(false);
    try {
      // Returns null for 404 (new user needing onboarding).
      // Throws for transient errors (network, 500, etc).
      await fetchUserAccount();
    } catch (error) {
      console.error('OnboardedLayout: Failed to fetch user data:', error);
      setFetchError(true);
    } finally {
      setUserDataLoading(false);
    }
  }, [isSessionValid, user, loading, fetchUserAccount]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  if (loading || userDataLoading) {
    return (
      <Layout title="">
        <LoadingBar visible />
      </Layout>
    );
  }

  if (!isSessionValid) {
    return <Navigate to="/auth" replace />;
  }

  // Fetch failed — show retry instead of wrongly redirecting to onboarding
  if (!user && fetchError) {
    return (
      <Layout title="">
        <div className="bg-destructive text-destructive-foreground rounded-md p-6 text-center max-w-sm mx-auto">
          <p className="mb-4">{t('articles.load_article_error')}</p>
          <button
            onClick={loadUserData}
            className="px-4 py-2 text-sm rounded bg-destructive-foreground text-destructive hover:opacity-90 transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </Layout>
    );
  }

  // No user account after successful fetch — genuinely needs onboarding
  if (!user) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!user.stripeCustomerId) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <BackgroundLoaderProvider>
      <VocabProvider>
        <Outlet />
      </VocabProvider>
    </BackgroundLoaderProvider>
  );
}
